import { createSlice } from '@reduxjs/toolkit'
import {
  resetTimeFilters,
  setCurrentFilterInfo,
  setInitialFilterInfo,
  updateTimeFilters,
} from '../../../actions'
import { defaultSelectedTab } from '../../../constants/constants'
import { getMaxSelectedStops } from './helpers/getMaxSelectedStops'
import { cloneDeep } from 'lodash'

/*
This reducer function takes in the data that comes from the individual filters, which in turn
comes from the @pcln-filter common filter component.  The individual filter reducers (stopsFilters, airlineFilters, etc)
keep the data in the format the FilterComponent needs.

The AirSearchRequest however needs the data transformed into an object of a different shape
(state.filterInfo.values) and this reducer handles this transformation and state updates.
*/

export const filterInfo = createSlice({
  name: 'filterInfo',
  initialState: {},
  reducers: {
    updateAirlineFiltersReq: (state, action) => {
      let latestFilterChange = {}
      const { values: prevValues, sliceFilters: prevSliceFilters } = state
      const { airlines = {}, payload = {}, meta = {}, sbsParams = {} } = action.payload
      const { isNoneClick, isAllClick, isOnlyClick } = meta
      const sliceIndex = sbsParams.activeTabId || defaultSelectedTab
      const changedValue = payload && payload.value
      const changedChecked = payload && payload.checked
      const includedCarriers = []
      const excludedCarriers = []

      airlines.forEach((airline) => {
        const { value, checked } = airline
        if (isAllClick || (isOnlyClick && value === changedValue)) {
          includedCarriers.push(value)
        } else if (isNoneClick || (isOnlyClick && value !== changedValue)) {
          excludedCarriers.push(value)
        } else if (value === changedValue) {
          if (changedChecked) {
            includedCarriers.push(value)
          } else {
            excludedCarriers.push(value)
          }
        } else {
          if (checked) {
            includedCarriers.push(value)
          } else {
            excludedCarriers.push(value)
          }
        }
      })

      latestFilterChange = {
        ['includedCarriers' + sliceIndex]: includedCarriers,
        ['excludedCarriers' + sliceIndex]: excludedCarriers,
      }

      // TODO: Use 'sliceFilters' to build filter object for various slices
      const sliceFilters = prevSliceFilters.map((slice, index) => {
        slice = { ...slice }
        if (index === sliceIndex) {
          if (excludedCarriers.length) {
            slice.excludedCarriers = excludedCarriers
            slice.includedCarriers = includedCarriers
          } else {
            delete slice.excludedCarriers
            delete slice.includedCarriers
          }
        }

        return slice
      })

      const updatedAirlineValues = Object.assign({}, prevValues, latestFilterChange)

      return {
        ...state,
        latestFilterChange,
        sliceFilters,
        values: updatedAirlineValues,
      }
    },
    updateAttributesFiltersReq: (state, action) => {
      let latestFilterChange = {}
      const { attributes = [], payload = {}, meta = {}, sbsParams = {} } = action.payload
      const { values: prevValues, sliceFilters: prevSliceFilters } = state
      const sliceIndex = sbsParams.activeTabId || defaultSelectedTab
      const { isAllClick, isOnlyClick } = meta
      const changedAttrValue = payload?.value
      const changedAttrChecked = payload.checked
      const includedAttributes = []

      attributes.forEach((attribute) => {
        const { value, checked } = attribute
        if (isAllClick || (isOnlyClick && value === changedAttrValue)) {
          includedAttributes.push(value)
        }
        // In the current payload, the attribute has changed
        // Do not simplify below if-else to a single if, there are some edge cases
        else if (value === changedAttrValue) {
          // if checked is true, add it
          if (changedAttrChecked) {
            includedAttributes.push(value)
          }
        }
        // In the current payload, the attribute has not changed
        else {
          // but attribute was checked previously, so add it
          if (checked) {
            includedAttributes.push(value)
          }
        }
      })

      const attrSliceFilters = prevSliceFilters.map((slice) => {
        slice = { ...slice }
        slice.includedAttributes = includedAttributes
        return slice
      })

      latestFilterChange = {
        ['attributes' + sliceIndex]: includedAttributes,
      }

      const updatedAttributeValues = Object.assign({}, prevValues, latestFilterChange)

      return {
        ...state,
        latestFilterChange,
        sliceFilters: attrSliceFilters,
        values: updatedAttributeValues,
      }
    },
    updateSelectedFareBrandFilters: (state, action) => {
      const { sliceFilters: prevSliceFilters } = state
      const { selectedFareBrand } = action.payload
      const fareBrandFilters = prevSliceFilters.map((slice) => {
        slice = { ...slice }
        if (slice.sliceId === selectedFareBrand?.sliceId) {
          slice.selectedFareBrand = selectedFareBrand
        }
        return slice
      })
      return {
        ...state,
        sliceFilters: fareBrandFilters,
      }
    },
    updateStopsFiltersReq: (state, action) => {
      state = { ...state }
      const { payload = {}, meta = {}, sbsParams = {} } = action.payload
      const sliceIndex = sbsParams.activeTabId || defaultSelectedTab
      const selectedStops = 'selectedStops' + sliceIndex

      if (meta.isNoneClick) {
        // Set all selected options to false in preparation for subsequent selections to represent that
        // each possible value is un-checked. This accounts for user clicking 'none' before any other stop filters.
        state.values[selectedStops] = Object.keys(state.values[selectedStops]).reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})

        return state
      }

      let stopsSelected = Object.assign({}, state.values[selectedStops], payload)

      const numOfStops = getMaxSelectedStops(state.values[selectedStops], payload, meta)

      if (meta?.isOnlyClick) {
        stopsSelected = { 0: false, 1: false, 7: false }
        stopsSelected[numOfStops] = true
      }

      const stopsKey = 'numOfStops' + sliceIndex
      return {
        ...state,
        latestFilterChange: { [stopsKey]: numOfStops },
        values: {
          ...state.values,
          [stopsKey]: numOfStops,
          [selectedStops]: stopsSelected,
        },
      }
    },
    updateAirportsFiltersReq: (state, action) => {
      const { payload = {}, meta = {}, entity, sbsParams = {} } = action.payload
      const latestFilterChange = {}
      const updatedAirportValues = { ...state.values }
      const [airportCode, shouldAdd] = payload
      const i = sbsParams.activeTabId
      const originSet = new Set(updatedAirportValues[`origAirports${i}`])
      const destSet = new Set(updatedAirportValues[`destAirports${i}`])
      if (meta.isAllClick) {
        const latestFilterChange = {}
        const [{ destinationAirports, originAirports }] = state?.defaults?.AIRPORTS
        const i = sbsParams.activeTabId
        latestFilterChange[`origAirports${i}`] = originAirports.map((i) => i.value)
        latestFilterChange[`destAirports${i}`] = destinationAirports.map((i) => i.value)
        return {
          ...state,
          latestFilterChange,
          values: latestFilterChange,
        }
      }
      if (meta.isNoneClick) {
        const latestFilterChange = {}
        const i = sbsParams.activeTabId
        latestFilterChange[`origAirports${i}`] = []
        latestFilterChange[`destAirports${i}`] = []
        return {
          ...state,
          latestFilterChange,
          values: latestFilterChange,
        }
      }
      const activeSet = entity === 'origin' ? originSet : destSet
      if (meta.isOnlyClick) {
        activeSet.clear()
        activeSet.add(airportCode)
      } else {
        if (shouldAdd) {
          activeSet.add(airportCode)
        } else {
          activeSet.delete(airportCode)
        }
      }

      updatedAirportValues[`origAirports${i}`] = [...originSet.values()]
      latestFilterChange[`origAirports${i}`] = [...originSet.values()]
      updatedAirportValues[`destAirports${i}`] = [...destSet.values()]
      latestFilterChange[`destAirports${i}`] = [...destSet.values()]

      return {
        ...state,
        latestFilterChange,
        values: updatedAirportValues,
      }
    },
    updateIncludedCarriers: (state, action) => {
      const latestFilterChange = {}
      const { payload = '' } = action
      const { values: prevValues } = state
      const includedAirlines = {
        includedCarriers: payload.split(','),
      }
      return {
        ...state,
        latestFilterChange,
        values: Object.assign({}, prevValues, includedAirlines),
      }
    },
    updateFilterInfoSortOptions: (state, action) => {
      return {
        ...state,
        defaults: {
          ...state.defaults,
          SORT_OPTIONS: action.payload,
        },
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetTimeFilters, (state, action) => {
      const { entity } = action.payload
      state = { ...state }
      const values = { ...state.values }
      let latestFilterChange = {}
      state.defaults.TIME.forEach((slice) => {
        const key = `timebands${slice?.sliceRefId}`
        const latestTimeBand = values[key]

        function createNewBand(entity, latestTimeBand) {
          return {
            takeoff: entity === 'takeoff' ? [] : latestTimeBand?.takeoff,
            landing: entity === 'landing' ? [] : latestTimeBand?.landing,
          }
        }
        const newBand = createNewBand(entity, latestTimeBand)
        values[key] = newBand
        latestFilterChange[key] = newBand
      })
      state.lastReset = Date.now()
      return {
        ...state,
        values,
        latestFilterChange,
      }
    })
    builder.addCase(updateTimeFilters, (state, action) => {
      if (!state.values) {
        return state
      }
      state = { ...state }
      const { values: prevValues } = state
      const { payload = {}, sliceRefId } = action.payload
      let latestFilterChange = {}

      let timebands = []

      if (payload?.takeoff || payload?.landing) {
        timebands = payload
        latestFilterChange[`timebands${sliceRefId}`] = payload
      }

      return {
        ...state,
        latestFilterChange,
        values: Object.assign({}, prevValues, latestFilterChange, {
          [`timebands${sliceRefId}`]: timebands,
        }),
      }
    })
    builder.addCase(setCurrentFilterInfo, (state, action) => {
      const { filterInfo = {} } = action.payload
      if (filterInfo) {
        const { values, defaults, originalValues, sliceFilters } = filterInfo
        state.values = values
        state.defaults = defaults
        state.originalValues = originalValues
        state.sliceFilters = sliceFilters
      }
    })
    builder.addCase(setInitialFilterInfo, (state, action) => {
      const { payload, sbsParams } = action.payload
      let prevValues = cloneDeep(state?.values || {})
      const sliceIndex = sbsParams.activeTabId || defaultSelectedTab
      let initialValuesObj = {}
      let defaults = payload

      /**
       * 'prevTab' is available when user transition from 1 tab to another and
       *  select the same itin. At this point SET_INITIAL_FILTER_INFO is called with filter values from cache
       * as a new airSearch call is not made
       */
      if (sbsParams.prevTab) {
        defaults = payload.filterDefaults
        initialValuesObj = { ...prevValues }
      } else {
        // Reset originalValues for Return Tab
        const keys = [
          'selectedStops',
          'numOfStops',
          'includedCarriers',
          'excludedCarriers',
          'destAirports',
          'origAirports',
          'attributes',
          'timebands',
        ]

        const numOfSlices = sliceIndex

        if (prevValues) {
          initialValuesObj = prevValues
        }

        for (let i = 1; i <= numOfSlices; i++) {
          keys.forEach((key) => {
            if (!initialValuesObj.hasOwnProperty(`${key}${i}`)) {
              if (key === 'selectedStops') {
                initialValuesObj[`${key}${i}`] = { 0: true, 1: true, 7: true }
              } else if (key === 'numOfStops') {
                initialValuesObj[`${key}${i}`] = '7'
              } else if (key === 'timebands') {
                initialValuesObj[`${key}${i}`] = null
              } else {
                initialValuesObj[`${key}${i}`] = null
              }
            }
          })
        }

        // Rebuild stops request state from API defaults so leftover selectedStops
        // keys (e.g. after employeeDiscountToggle) cannot keep numOfStops stuck at 7
        const stopsSelectedSliceIndex = 'selectedStops' + sliceIndex
        const stopsMap = { 0: false, 1: false, 7: false }
        payload?.NUM_OF_STOPS?.forEach((stop) => {
          if (stop?.value !== undefined && stop?.value !== null) {
            stopsMap[stop.value] = stop.checked
          }
        })
        initialValuesObj[stopsSelectedSliceIndex] = stopsMap
        initialValuesObj['numOfStops' + sliceIndex] = getMaxSelectedStops(stopsMap, {}, undefined) || '7'

        const initialIncludedCarriers = payload?.AIRLINES?.map((airline) => airline.value)
        initialValuesObj = Object.assign(initialValuesObj, {
          [`includedCarriers${sliceIndex}`]: initialIncludedCarriers,
        })

        initialValuesObj = Object.assign(initialValuesObj, {
          [`excludedCarriers${sliceIndex}`]: [],
        })

        payload?.AIRPORTS?.forEach((airportObj) => {
          initialValuesObj['destAirports' + sliceIndex] = airportObj?.destinationAirports
            ?.map((airport) => (airport.checked ? airport.value : null))
            .filter(Boolean)
          initialValuesObj['origAirports' + sliceIndex] = airportObj?.originAirports
            ?.map((airport) => (airport.checked ? airport.value : null))
            .filter(Boolean)
        })
      }

      return {
        ...state,
        lastReset: Date.now(),
        values: initialValuesObj,
        defaults,
        originalValues: initialValuesObj,
      }
    })
  },
})

export const {
  updateAirlineFiltersReq,
  updateAttributesFiltersReq,
  updateSelectedFareBrandFilters,
  updateStopsFiltersReq,
  updateAirportsFiltersReq,
  updateIncludedCarriers,
  updateFilterInfoSortOptions,
} = filterInfo.actions
